import React from 'react';
import styled from 'styled-components';
import { Box, Button, Link, Paragraph, Text } from '@99designs/emu';
import { colors, spaces } from '@99designs/emu/settings';
import { IconSmallBullet1, IconStar2 } from '@99designs/icons';
import { UserAvatar } from '@99designs/users';
import { profilePaths } from '../../../profileHelper';
import { LocalTime } from '../Designer';
import { Designer } from './types';

export interface DesignerDetailsHeaderProps {
    designer: Designer;
    onSelectDesigner: () => void;
    jobId?: string;
}

const DesignerDetailsHeaderWrapper = styled.div`
    overflow-y: auto;
`;

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: ${spaces['1.5bu']};
`;

const Meta = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: ${spaces['3bu']};

    > * {
        margin-right: ${spaces['1bu']};

        &:last-child {
            margin-right: 0;
        }
    }
`;

const RatingContainer = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;
    gap: ${spaces['0.25bu']};
`;

const RatingStar = styled(IconStar2)`
    height: 16px;
    width: 16px;
    color: ${colors.grey800};
`;

function DesignerDetailsHeader(props: DesignerDetailsHeaderProps) {
    const { designer, onSelectDesigner, jobId } = props;
    const { reviewRating, reviewCount, utcOffset } = props.designer.profile;
    const paths = profilePaths(designer.id, jobId);

    return (
        <DesignerDetailsHeaderWrapper>
            <Panel>
                <Meta>
                    <Box alignSelf="center">
                        <UserAvatar userId={designer.id} size="medium" />
                    </Box>
                    <Box>
                        <Link
                            href={paths.base}
                            underline="hover"
                            color="grey800"
                            target="_blank"
                            data-testid="display-name"
                        >
                            <Text
                                italic
                                data-qm-block
                                fontFamily="attribution"
                                size="size4"
                                lineHeight="h1"
                            >
                                {designer.displayName}
                            </Text>
                        </Link>
                        <Paragraph as="div" size="small">
                            <LocalTime utcOffset={utcOffset} />
                        </Paragraph>
                        {reviewRating > 0 && (
                            <Box display="flex">
                                <RatingContainer data-testid="designer-review-rating">
                                    <RatingStar />
                                    <Text size="size2">{reviewRating}</Text>
                                    <IconSmallBullet1 />
                                    <Link
                                        href={paths.about}
                                        color="grey800"
                                        target="_blank"
                                        size="size2"
                                        data-testid="designer-review-link"
                                    >
                                        {reviewCount} reviews
                                    </Link>
                                </RatingContainer>
                            </Box>
                        )}
                    </Box>
                </Meta>
                <Box>
                    <Button onClick={onSelectDesigner} type="submit" width="fill">
                        Select {designer.displayName}
                    </Button>
                </Box>
            </Panel>
        </DesignerDetailsHeaderWrapper>
    );
}

export default DesignerDetailsHeader;
